import {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";

import AutoComplete from "../../../AutoComplete";
import Checkbox from "../../../Checkbox";

import {
  useGetCDKShipViaDefault,
  useGetCDKUniqueShipVia,
  usePostNewCDKShipViaDefault,
  useUpdateCDKShipViaDefault,
} from "../cdkSettingsPage.utils";

const isNonNullable = (value) => value !== undefined && value !== null;

const isNullable = (value) => value === undefined || value === null;

const formatCurrentShipViaValues = (shipViaDefaultData) => {
  if (!shipViaDefaultData) return [];

  return shipViaDefaultData.map((item) => ({
    label: item.filterName,
    value: item.filterName,
    isVisible: item.isVisible,
    isDedicated: item.isDedicated,
    id: item.id,
  }));
};

const formatUniqueShipViaValues = (uniqueShipViaData) => {
  if (!uniqueShipViaData) return [];

  const filteredUniqueFilterValues = uniqueShipViaData.filter(isNonNullable);

  const containsNull = uniqueShipViaData.some(isNullable);

  const formatFilteredUniqueFilterValues = filteredUniqueFilterValues.map(
    (item) => ({
      label: item === "" ? "[Blank]" : item,
      value: item,
    })
  );

  const isBlankExist = formatFilteredUniqueFilterValues.some(
    (item) => item.label === "[Blank]"
  );

  return formatFilteredUniqueFilterValues;
  // return containsNull && !isBlankExist
  //   ? [{ label: "[Blank]", value: "" }, ...formatFilteredUniqueFilterValues]
  //   : formatFilteredUniqueFilterValues;
};

const ShipViaFilter = forwardRef(function ShipViaFilter(
  { clientID, isDedicated, disabled },
  ref
) {
  const { data: shipviaDefaultData, isLoading: isShipviaDefaultDataLoading } =
    useGetCDKShipViaDefault();

  const {
    data: uniqueShipViaValuesData,
    isLoading: isUniqueShipViaValuesDataLoading,
  } = useGetCDKUniqueShipVia();

  const shipviaMutationUpdate = useUpdateCDKShipViaDefault();

  const shipviaMutationPost = usePostNewCDKShipViaDefault();

  const [currentShipViaValues, setCurrentShipViaValues] = useState([]);

  useEffect(() => {
    if (!shipviaDefaultData || typeof shipviaDefaultData === "string") return; // in case response is a string

    if (shipviaDefaultData.length === 0) return;

    // filter out the default filters that are not visible

    const visibleDefaultShipviaFilters = formatCurrentShipViaValues(
      shipviaDefaultData || []
    ).filter(
      (filter) =>
        filter.isVisible &&
        (isDedicated ? filter.isDedicated === true : !filter.isDedicated)
    );

    setCurrentShipViaValues(visibleDefaultShipviaFilters);
  }, [shipviaDefaultData]);

  const handleSaveAutoCompleteShipVia = useCallback((options) => {
    if (!options) return;

    const newFilterOptions = options.length > 0 ? options : [];

    setCurrentShipViaValues(newFilterOptions);
  }, []);

  const handleOnSubmitShipVia = () => {
    if (isUniqueShipViaValuesDataLoading && isShipviaDefaultDataLoading) return; // Prevents saving when data is still loading
    if (!clientID || !currentShipViaValues || !shipviaDefaultData) return; // Prevents saving when clientID is not available or currentShipViaValues is empty
    // console.log("current ship via values", currentShipViaValues);
    const currentAutoCompleteShipViaValues = currentShipViaValues.map(
      (item) => item?.value
    );
    // console.log(
    //   "current ship via autocomplete values",
    //   currentAutoCompleteShipViaValues
    // );
    const previousCurrentShipviaValues = formatCurrentShipViaValues(
      shipviaDefaultData
    ).filter((itemToFilter) =>
      isDedicated ? itemToFilter.isDedicated : !itemToFilter.isDedicated
    );

    // console.log(
    //   "previous current ship via values",
    //   previousCurrentShipviaValues
    // );
    // Post New Shipvia Values only if there are new values meaning the currentAutoCompleteShipViaValues are not in the database

    const newShipViaValues = currentAutoCompleteShipViaValues?.filter(
      (item) =>
        !previousCurrentShipviaValues?.some(
          (prevItem) => prevItem.value === item
        )
    );
    // console.log("new ship via values", newShipViaValues);

    const formatPostNameFilterValues = newShipViaValues?.map((item, index) => ({
      Id: index,
      ClientId: clientID,
      FilterName: item,
      isDedicated: isDedicated,
    }));

    // Update Shipvia Values only if isVisible is different from the previous values and the currentAutoCompleteShipViaValues are in the database

    const deselectedShipViaValues = previousCurrentShipviaValues
      .filter(
        (prevItem) =>
          !currentAutoCompleteShipViaValues?.includes(prevItem.value) &&
          prevItem.isVisible
      )
      .map((item) => ({
        Id: item.id,
        FilterName: item.value,
        isVisible: false,
        isDedicated: isDedicated,
      }));
    // console.log("deselected ship via values", deselectedShipViaValues);

    const selectedShipViaValues = previousCurrentShipviaValues
      ?.filter(
        (prevItem) =>
          currentAutoCompleteShipViaValues.includes(prevItem.value) &&
          !prevItem.isVisible
      )
      .map((item) => ({
        Id: item.id,
        FilterName: item.value,
        isVisible: true,
        isDedicated: isDedicated,
      }));
    // return;
    // Post New Shipvia Values
    if (newShipViaValues.length > 0) {
      shipviaMutationPost.mutate(formatPostNameFilterValues);
    }
    // Update Shipvia Values
    const updateShipValuesDefault = [
      ...deselectedShipViaValues,
      ...selectedShipViaValues,
    ];

    if (updateShipValuesDefault.length > 0) {
      shipviaMutationUpdate.mutate(updateShipValuesDefault);
    }
  };

  useImperativeHandle(ref, () => ({
    handleOnSubmitShipVia,
  }));

  return (
    <AutoComplete
      multiple
      freesolo
      options={formatUniqueShipViaValues(uniqueShipViaValuesData || [])}
      getOptionLabel={(option) => option?.label || ""}
      defaultValue={currentShipViaValues}
      handleBlurSave={handleSaveAutoCompleteShipVia}
      noOptionsText="No options found, ENTER a new ship via filter value"
      renderOption={(props, option, selected) => (
        <li {...props}>
          <Checkbox
            inputClassName="form-check-input"
            name={option.value || option.label}
            label={option.label}
            checked={selected}
            readOnly
          />
        </li>
      )}
      disabled={disabled}
      loading={isShipviaDefaultDataLoading || isUniqueShipViaValuesDataLoading}
      renderInput={(params) => (
        <input {...params} placeholder="Search and Select" />
      )}
    />
  );
});

export default ShipViaFilter;
